// clone-repo-with-recovery-menu.ts
//
// v3: Wrap clone repo vào src/ với recovery menu khi gặp vấn đề (repo not found,
// permission denied, network). Thay vì để git văng lỗi thô + crash, verify remote
// TRƯỚC clone → classify lỗi → menu: retry / nhập URL khác / switch account / bỏ.
//
// Cũng dọn thư mục src/<name> dở nếu clone fail giữa chừng (tránh "đã tồn tại"
// khi user retry).

import { join } from "node:path";
import type { AddRepoFlags } from "./add-repo-non-interactive-flags.js";
import {
  type CloneRepoResult,
  cloneCodeRepoIntoSrc,
  RepoAlreadyInWorkspaceError,
} from "./clone-code-repo-into-src.js";
import { removeRecursive } from "./filesystem-helpers.js";
import { isGithubHost, parseGitRemoteHost } from "./git-remote-host.js";
import { NonInteractiveAbortError } from "./non-interactive-abort-error.js";
import { MissingFlagError } from "./non-interactive-context.js";
import { promptAndStorePatForHost } from "./prompt-and-store-pat-for-non-github-host.js";
import { confirmOrFlag, inputOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";
import { triggerGhCliAuthLogin } from "./trigger-gh-cli-auth-login.js";
import { tryVerifyGitRemoteAccessible } from "./verify-git-remote-accessible.js";

// Hướng dẫn theo từng loại lỗi remote.
function explainRemoteFailure(url: string, reason: string, detail?: string): void {
  log.warn(`⛔ Không clone được ${url}`);
  switch (reason) {
    case "not-found":
      log.dim(
        isGithubHost(parseGitRemoteHost(url))
          ? "  → Repo không tồn tại (sai URL, hoặc repo chưa được tạo trên GitHub)."
          : `  → Repo không tồn tại trên ${parseGitRemoteHost(url) ?? "host này"} (sai URL/path, hoặc repo chưa được tạo).`,
      );
      break;
    case "no-access":
      if (isGithubHost(parseGitRemoteHost(url))) {
        log.dim(
          "  → Không có quyền truy cập (repo private + sai account), HOẶC repo không tồn tại.\n" +
            "    Kiểm tra: đang login đúng GitHub account chưa? (gh auth status)",
        );
      } else {
        // Host không phải GitHub (GitLab self-host, Bitbucket, …): `gh auth status`
        // vô nghĩa ở đây — thứ thiếu là credential cho CHÍNH host đó.
        log.dim(
          "  → Không có quyền truy cập (repo private, chưa có credential cho host này), HOẶC repo không tồn tại.\n" +
            "    HTTPS cần Personal Access Token (scope read_repository) — hoặc dùng URL SSH nếu đã đăng ký SSH key.",
        );
      }
      break;
    case "network":
      log.dim("  → Lỗi mạng (mất kết nối / DNS / timeout).");
      break;
    default:
      log.dim(`  → Lỗi không xác định${detail ? `: ${detail}` : ""}.`);
  }
}

// Switch account → dùng triggerGhCliAuthLogin authoritative (đủ flags --hostname
// --git-protocol ssh, nhất quán với init flow). Catch để warn-only (recovery loop
// tự re-verify sau).
function switchGhAccount(): void {
  log.info("Mở browser để switch GitHub account...");
  try {
    triggerGhCliAuthLogin();
  } catch (err) {
    log.warn(
      `${err instanceof Error ? err.message : err}. Có thể chạy 'gh auth login' tay rồi retry.`,
    );
  }
}

const ADD = "add repo";
const CLONE_FAIL_FLAG_SPEC = {
  flag: "--on-clone-fail",
  command: ADD,
  hint: "--on-clone-fail abort|retry|skip",
};
const EXISTING_DIR_FLAG_SPEC = {
  flag: "--on-existing-dir",
  command: ADD,
  hint: "--on-existing-dir abort|use|backup",
};
const NEW_DIR_NAME_FLAG_SPEC = { flag: "--name", command: ADD, hint: "--name ten-thu-muc-khac" };
const NEW_URL_FLAG_SPEC = { flag: "--url", command: ADD, hint: "--url <url-khac>" };
const REMOTE_ACCESS_FLAG = "--on-remote-access-fail";

// Lý do verify fail mà credential có thể chữa được. `unknown`/`timeout` cũng vào đây
// vì một số host trả lỗi auth dưới dạng không phân loại được.
const AUTH_REASONS = new Set(["no-access", "unknown", "timeout"]);

// #300 — trần số lần `--on-clone-fail retry` ở chế độ non-interactive. Không có ai
// ngồi trước máy để chọn khác `retry`, nên lỗi BỀN (URL sai, mất mạng, chưa có
// credential) sẽ lặp vô hạn tới khi watchdog giết tiến trình. Trần là đủ để thoát;
// không backoff (YAGNI).
export const MAX_NON_INTERACTIVE_CLONE_RETRIES = 3;

// Hint cho JSON abort khi host ≠ GitHub gặp lỗi auth: nêu ĐÚNG 3 cờ để phía máy
// (extension) chạy lại được mà không phải đoán — đây là đường thoát duy nhất khi
// không có ai ngồi trước máy để chọn "Nhập PAT" trong menu.
function patNonInteractiveHint(host: string): string {
  return (
    `${REMOTE_ACCESS_FLAG} use-pat --pat-user <username> --pat-stdin ` +
    `(PAT scope read_repository cho ${host}), hoặc dùng URL SSH đã đăng ký key`
  );
}

// Menu recovery khi clone fail. Trả action user chọn.
// url dùng để detect host → quyết định offer PAT (non-GitHub) hay switch-account (GitHub).
async function promptCloneRecovery(
  reason: string,
  url: string,
  flags?: AddRepoFlags,
  detail?: string,
  patAlreadyAttempted = false,
  retriesSoFar = 0,
): Promise<"retry" | "new-url" | "switch-account" | "enter-pat" | "skip"> {
  const host = parseGitRemoteHost(url);
  const isGh = isGithubHost(host);
  // PAT chỉ có nghĩa khi lỗi thuộc loại auth VÀ host không phải GitHub (GitHub đi
  // đường gh) — cùng điều kiện với lựa chọn "Nhập PAT" của menu tương tác bên dưới.
  const patEligible = AUTH_REASONS.has(reason) && !isGh && host !== null;

  // Non-interactive: menu recovery không có ai bấm. `--on-clone-fail` quyết định,
  // mặc định `abort` — tự `retry`/`skip` khi không ai nhìn là cách tạo trạng thái
  // nửa vời trên đĩa (thư mục clone dở, manifest lệch).
  if (flags) {
    // `--on-remote-access-fail use-pat` (+ `--pat-user`/`--pat-stdin`): cờ đã đăng ký
    // từ T3 nhưng trước đây CHỈ nhánh new/folder đọc — nhánh clone URL (đường
    // extension đi) không bao giờ tới được `enter-pat`, nên repo private trên
    // GitLab self-host luôn dừng ở `clone_failed` dù người gọi đã đưa PAT.
    if (patEligible && flags.onRemoteAccessFail === "use-pat") {
      if (!patAlreadyAttempted) return "enter-pat";
      // Lần 2 vẫn fail sau khi đã lưu PAT: DỪNG với lý do thật. Không được để rơi
      // xuống `promptAndStorePatForHost` lần nữa — stdin đã cạn nên nó sẽ ném
      // `MissingFlagError --pat-stdin` cho một cờ ĐANG CÓ mặt (đúng lỗi #293).
      throw new NonInteractiveAbortError({
        command: ADD,
        reason: "clone_failed",
        abortedBy: REMOTE_ACCESS_FLAG,
        detail: detail ?? `${reason} (${url})`,
        hint:
          `PAT đã lưu cho ${host} nhưng remote vẫn từ chối — kiểm username, scope ` +
          "read_repository, và quyền của tài khoản trên repo này",
      });
    }
    // ⚠️ #293: `undefined` = cờ VẮNG trong argv, khác hẳn `"abort"` do người gọi
    // truyền tay. Gộp hai ca vào một lỗi thì JSON khai `missing_flag` cho cờ đang
    // CÓ mặt — phía máy thêm lại cờ đó rồi hỏng vòng hai.
    const chosen = flags.onCloneFail;
    if (chosen === undefined) {
      throw new MissingFlagError({
        missingFlag: "--on-clone-fail",
        command: "add repo",
        hint: "--on-clone-fail retry|skip (mặc định abort — dừng, không để lại thư mục dở)",
      });
    }
    if (chosen === "abort") {
      throw new NonInteractiveAbortError({
        command: ADD,
        reason: "clone_failed",
        abortedBy: "--on-clone-fail",
        detail: detail ?? `${reason} (${url})`,
        hint: patEligible
          ? patNonInteractiveHint(host)
          : "Chữa URL/mạng/credential, hoặc đổi sang --on-clone-fail retry|skip",
      });
    }
    if (chosen === "skip") return "skip";
    // `retry` quá trần ⇒ DỪNG với lý do thật (#300). Hint nêu đã thử N lần + đường
    // PAT khi host ≠ GitHub — cùng nội dung với nhánh `abort` để phía máy đọc được.
    if (retriesSoFar >= MAX_NON_INTERACTIVE_CLONE_RETRIES) {
      throw new NonInteractiveAbortError({
        command: ADD,
        reason: "clone_failed",
        abortedBy: "--on-clone-fail",
        detail: detail ?? `${reason} (${url})`,
        hint:
          `Đã retry ${MAX_NON_INTERACTIVE_CLONE_RETRIES} lần mà remote vẫn lỗi (${reason}) — ` +
          (patEligible
            ? patNonInteractiveHint(host)
            : "chữa URL/mạng/credential rồi chạy lại, hoặc đổi sang --on-clone-fail skip"),
      });
    }
    return "retry";
  }

  const choices: Array<{ name: string; value: string }> = [
    { name: "Thử lại (retry)", value: "retry" },
    { name: "Nhập URL repo khác", value: "new-url" },
  ];

  if (AUTH_REASONS.has(reason)) {
    if (isGh) {
      // GitHub: offer switch account qua gh auth login
      choices.push({
        name: "Switch GitHub account (gh auth login — mở browser)",
        value: "switch-account",
      });
    } else if (host) {
      // Non-GitHub (GitLab, Bitbucket, Gitea, ...): offer nhập PAT
      choices.push({
        name: `Nhập Personal Access Token cho ${host} (lưu vào keychain)`,
        value: "enter-pat",
      });
    }
  }

  choices.push({ name: "Bỏ qua repo này", value: "skip" });

  return (await selectOrFlag(undefined, CLONE_FAIL_FLAG_SPEC, {
    message: "Cách xử lý?",
    choices,
  })) as "retry" | "new-url" | "switch-account" | "enter-pat" | "skip";
}

export interface CloneWithRecoveryResult {
  cloned: CloneRepoResult | null; // null nếu user skip.
  skipped: boolean;
}

// Clone repo vào src/<name> với recovery menu. url/name có thể đổi qua menu.
// Trả { cloned, skipped } — skipped=true nếu user chọn "Bỏ qua repo này".
export async function cloneRepoWithRecovery(args: {
  workspaceRoot: string;
  url: string;
  name: string;
  /** Cờ non-interactive (T3). `undefined` = có người ngồi trước máy. */
  nonInteractiveFlags?: AddRepoFlags;
}): Promise<CloneWithRecoveryResult> {
  let url = args.url;
  let name = args.name;
  const flags = args.nonInteractiveFlags;
  // Đã thử lưu PAT chưa (dù lưu thành công hay không). Non-interactive dùng để chặn
  // vòng lặp verify→PAT→verify; tương tác thì menu tự hỏi lại nên không cần.
  let patAttempted = false;
  // Số lần đã `retry` (verify fail HOẶC clone fail thật). Chỉ nhánh non-interactive
  // đọc để áp trần #300; tương tác thì người dùng tự chọn nên không cần trần.
  let retries = 0;

  while (true) {
    // 1. Verify remote accessible TRƯỚC clone (tránh git văng lỗi thô + thư mục dở).
    const verify = tryVerifyGitRemoteAccessible(url);
    if (!verify.ok) {
      explainRemoteFailure(url, verify.reason ?? "unknown", verify.detail);
      const action = await promptCloneRecovery(
        verify.reason ?? "unknown",
        url,
        flags,
        verify.detail,
        patAttempted,
        retries,
      );

      if (action === "skip") {
        log.dim(`Bỏ qua repo ${name}.`);
        return { cloned: null, skipped: true };
      }
      if (action === "switch-account") {
        switchGhAccount();
        continue; // re-verify với account mới.
      }
      if (action === "enter-pat") {
        const host = parseGitRemoteHost(url);
        patAttempted = true;
        if (host) {
          await promptAndStorePatForHost(host);
        }
        continue; // re-verify — git credential store mới sẽ được dùng.
      }
      if (action === "new-url") {
        url = await inputOrFlag(undefined, NEW_URL_FLAG_SPEC, {
          message: "URL repo khác:",
          validate: (v) => (v.trim().length > 0 ? true : "URL bắt buộc"),
        });
        // Hỏi đổi tên không (URL mới có thể tên khác).
        const renameToo = await confirmOrFlag(undefined, NEW_DIR_NAME_FLAG_SPEC, {
          message: `Đổi tên thư mục trong src/ theo URL mới? (hiện: ${name})`,
          default: false,
        });
        if (renameToo) {
          const { inferRepoNameFromUrl } = await import("./clone-code-repo-into-src.js");
          name = await inputOrFlag(undefined, NEW_DIR_NAME_FLAG_SPEC, {
            message: "Tên thư mục:",
            default: inferRepoNameFromUrl(url),
          });
        }
        continue;
      }
      // action === "retry" → loop lại verify.
      retries += 1;
      continue;
    }

    // 2. Remote OK → clone. Vẫn try/catch phòng clone fail sau verify (race).
    try {
      const cloned = await cloneCodeRepoIntoSrc({ workspaceRoot: args.workspaceRoot, url, name });
      return { cloned, skipped: false };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);

      // CRITICAL: lỗi PRE-CLONE (repo/tên đã tồn tại) → repo cũ KHÔNG bị git clone
      // đụng → TUYỆT ĐỐI KHÔNG xóa src/<name> (sẽ xóa nhầm repo cũ → mất data).
      // Báo lỗi + cho user đổi tên / bỏ qua. KHÔNG dọn thư mục.
      if (err instanceof RepoAlreadyInWorkspaceError) {
        // Repo đã tồn tại CÙNG remote = đã add rồi → CHẶN, không cho clone bản 2
        // (tránh 2 folder cùng remote → push đè nhau). Báo rõ + skip thẳng.
        const existingUrl = await readExistingRepoUrl(args.workspaceRoot, name);
        if (existingUrl && sameRemote(existingUrl, url)) {
          log.warn(
            `⚠ Repo này đã có trong workspace (src/${name}). Dùng trực tiếp, không add lại.`,
          );
          log.dim(`  Muốn thay bằng bản mới? Gỡ trước: avatar remove repo ${name}`);
          return { cloned: null, skipped: true };
        }

        // Trùng TÊN nhưng KHÁC remote (repo khác) → đổi tên là hợp lý.
        log.warn(`⚠ ${message}`);
        // Non-interactive: `--on-existing-dir` thay menu. `abort` (mặc định) ném
        // lỗi ⇒ thư mục cũ NGUYÊN VẸN, `repos.json` không đổi.
        if (flags) {
          // #293 — cùng loài với `--on-clone-fail`: cờ VẮNG mới là "thiếu cờ".
          const onExisting = flags.onExistingDir;
          if (onExisting === undefined) {
            throw new MissingFlagError({
              missingFlag: "--on-existing-dir",
              command: "add repo",
              hint: "--on-existing-dir use|backup (mặc định abort — giữ nguyên thư mục cũ)",
            });
          }
          if (onExisting === "abort") {
            throw new NonInteractiveAbortError({
              command: "add repo",
              reason: "existing_dir",
              abortedBy: "--on-existing-dir",
              detail: message,
              hint: "Đổi --name, hoặc dùng --on-existing-dir use|backup",
            });
          }
          if (onExisting === "use") return { cloned: null, skipped: true };
        }
        const action = await selectOrFlag(flags ? "rename" : undefined, EXISTING_DIR_FLAG_SPEC, {
          message: "Cách xử lý?",
          choices: [
            { name: "Đổi tên thư mục khác", value: "rename" },
            { name: "Bỏ qua repo này", value: "skip" },
          ],
        });
        if (action === "skip") return { cloned: null, skipped: true };
        name = await inputOrFlag(
          flags ? `${name}-backup-${Date.now()}` : undefined,
          NEW_DIR_NAME_FLAG_SPEC,
          {
            message: "Tên thư mục mới trong src/:",
            validate: (v) => (v.trim().length > 0 ? true : "Tên bắt buộc"),
          },
        );
        continue;
      }

      // Lỗi clone THẬT (git tạo src/<name> dở rồi fail) → dọn thư mục dở để retry sạch.
      log.warn(`Clone fail: ${message}`);
      await removeRecursive(join(args.workspaceRoot, "src", name)).catch(() => {});

      const action = await promptCloneRecovery(
        "unknown",
        url,
        flags,
        message,
        patAttempted,
        retries,
      );
      if (action === "skip") return { cloned: null, skipped: true };
      if (action === "switch-account") {
        switchGhAccount();
        continue;
      }
      if (action === "enter-pat") {
        const host = parseGitRemoteHost(url);
        patAttempted = true;
        if (host) {
          await promptAndStorePatForHost(host);
        }
        continue; // re-verify sau khi lưu credential.
      }
      if (action === "new-url") {
        url = await inputOrFlag(undefined, NEW_URL_FLAG_SPEC, {
          message: "URL repo khác:",
          validate: (v) => (v.trim().length > 0 ? true : "URL bắt buộc"),
        });
      } else {
        retries += 1; // action === "retry"
      }
      // retry / new-url → loop lại while(true).
    }
  }
}

// Đọc URL remote của repo đã có trong manifest (so sánh xem có cùng repo không).
async function readExistingRepoUrl(workspaceRoot: string, name: string): Promise<string | null> {
  const { readReposManifest } = await import("./workspace-repos-manifest-store.js");
  const repos = await readReposManifest(workspaceRoot);
  return repos.find((r) => r.name === name)?.url ?? null;
}

// So sánh 2 git URL có trỏ cùng repo không. Chuẩn hóa: bỏ scheme (ssh/https),
// .git suffix, trailing slash, lowercase. "git@github.com:o/r.git" ==
// "https://github.com/o/r/" → cùng repo.
function sameRemote(a: string, b: string): boolean {
  const norm = (u: string) =>
    u
      .trim()
      .toLowerCase()
      .replace(/^git@/, "")
      .replace(/^https?:\/\//, "")
      .replace(/:/, "/") // git@github.com:o/r → github.com/o/r
      .replace(/\.git$/, "")
      .replace(/\/+$/, "");
  return norm(a) === norm(b);
}
